import { AutoComplete, Tooltip, Typography } from "antd-v5";
import { DefaultOptionType } from "antd-v5/lib/select";
import AuthorisedContainer from "components/AuthorisedContainer";
import { preventEnter } from "components/TagTable";
import { getVocabulary } from "components/Vocabulary";
import { PermissionSetMap } from "constants/RBACConstants";
import { useFeatureFlag } from "contexts/FeatureFlagContext";
import usePaginationControls from "hooks/usePaginationControls";
import useTableCustomizationHelper from "hooks/useTableCustomizationHelper";
import useTagTableDetails, { Tag } from "hooks/useTagTableData";
import {
  filter,
  find,
  findIndex,
  flatten,
  isEmpty,
  isUndefined,
  map,
  noop,
} from "lodash";
import React, {
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useHistory } from "react-router-dom";
import { hasPermission, isSuperOrTenantAdmin } from "utils/authService";
import {
  getFindingDetailsTagsSortInfo,
  setFindingDetailsTagsSortInfo,
} from "utils/localStorageUtils";
import Sorters, { SorterFunction } from "utils/sorters";
import { getOrigin } from "views/Findings/FindingDetails/FindingDetailsTag";

import ACDropdown from "ac-components/ACDropdown";
import ACTable from "ac-components/ACTable/ACTable";
import ACButton from "ac-components/ac-button/ACButton";
import EditIcon from "ac-components/ac-icons/EditIcon";
import EllipsisVerticalIcon from "ac-components/ac-icons/EllipsisVerticalIcon";
import PlusIcon from "ac-components/ac-icons/PlusIcon";
import SaveIcon from "ac-components/ac-icons/SaveIcon";
import TrashIcon from "ac-components/ac-icons/TrashIcon";
import ACResizableColumnCell from "ac-components/ac-resizable-column-cell/ACResizableColumnCell";
import ACTabs, { ACTabItem } from "ac-components/ac-tabs/ACTabs";
import ACTag from "ac-components/ac-tag/ACTag";

import "./ACTagTable.less";

interface TagsMetaMap {
  [key: string]: string[];
}
interface ACTagTableProps {
  source: (string | Tag)[];
  staticTags: string[];
  editable: boolean;
  onChange: (
    source: (string | Tag)[],
    deleted: { deleted?: string | Tag },
  ) => void;
  getAllEvents: (source: (string | Tag)[], action: string) => void;
  extras: ReactNode;
  mentionOptions: string[];
  disableAccessText: string;
  tagsRef: RefObject<{ [key: string]: any }>;
  preventKeys: string[];
  accountAdminOnlyKeys?: string[];
  className: string;
  width: string;
  tagsMetaMap: TagsMetaMap;
  tabsList: ACTabItem[];
  searchTerm?: string;
  [key: string]: any;
}
export default function ACTagTable({
  source,
  staticTags,
  editable = false,
  onChange = noop,
  getAllEvents = noop,
  extras,
  mentionOptions,
  disableAccessText,
  tagsRef,
  preventKeys = [],
  accountAdminOnlyKeys = [],
  width = "200px",
  className,
  tagsMetaMap,
  tabsList,
  extraColumn,
  finding,
  searchTerm,
  ...props
}: ACTagTableProps) {
  const {
    dataSource,
    selectedTag,
    wrapperRef,
    currentRowRef,
    tagValue,
    onAdd,
    onKeyChange,
    onKeyInputChange,
    onDelete,
    toggleEdit,
    onSave,
  } = useTagTableDetails({
    source,
    onChange,
    getAllEvents,
    staticTags,
    preventKeys,
    accountAdminOnlyKeys,
    mentionOptions,
    tagsRef,
  });
  const [tabValue, setTabValue] = useState(tabsList[0].value);
  const savedSortInfo = getFindingDetailsTagsSortInfo();
  const { onSortChange, sortInfo } = usePaginationControls({
    defaultSortInfo: savedSortInfo,
  });
  const history = useHistory();

  useEffect(() => {
    setFindingDetailsTagsSortInfo(sortInfo);
  }, [sortInfo]);

  const featureFlags = useFeatureFlag();

  const tagsActionItems = useCallback(
    (record) => {
      const isAccountAdminOnlyKey = accountAdminOnlyKeys.some((key) =>
        record.key?.startsWith(key),
      );
      const isAccountAdminRestricted =
        isAccountAdminOnlyKey && !isSuperOrTenantAdmin();

      return [
        ...(getOrigin(record, tagsMetaMap) !== "FINDING"
          ? [
              {
                label: "Open Origin",
                value: "openOrigin",
                disabled:
                  getOrigin(record, tagsMetaMap) === "ASSETS" &&
                  !featureFlags.RBVM_ASSETS,
              },
            ]
          : []),
        {
          label: (
            <Tooltip
              title={
                !hasPermission(PermissionSetMap.UPDATE_FINDING_TAGS)
                  ? "No access"
                  : record.disable
                    ? "Cannot edit"
                    : isAccountAdminRestricted
                      ? "Only Account Admins can add/edit/remove armorcode.group tags"
                      : ""
              }
              placement="top"
            >
              <Typography.Text style={{ color: "#70737B" }}>
                Edit
              </Typography.Text>
            </Tooltip>
          ),
          value: "edit",
          icon: <EditIcon />,
          disabled:
            !hasPermission(PermissionSetMap.UPDATE_FINDING_TAGS) ||
            record.disable ||
            isAccountAdminRestricted,
        },
        {
          label: (
            <Tooltip
              title={
                !hasPermission(PermissionSetMap.UPDATE_FINDING_TAGS)
                  ? "No access"
                  : record.disable
                    ? "Cannot delete"
                    : isAccountAdminRestricted
                      ? "Only Account Admins can add/edit/remove armorcode.group tags"
                      : ""
              }
            >
              <Typography.Text style={{ color: "#D92C20" }}>
                Delete
              </Typography.Text>{" "}
              {/* color is different in figma thats why giving style and not type*/}
            </Tooltip>
          ),
          value: "delete",
          icon: <TrashIcon style={{ color: "#D92C20" }} />,
          disabled:
            !hasPermission(PermissionSetMap.UPDATE_FINDING_TAGS) ||
            record.disable ||
            isAccountAdminRestricted,
        },
      ];
    },
    [tagsMetaMap, featureFlags.RBVM_ASSETS, accountAdminOnlyKeys],
  );

  const OpenOrigin = useCallback(
    (currentRecord: Tag) => {
      const origin =
        tabValue === "ALL" ? getOrigin(currentRecord, tagsMetaMap) : tabValue;
      switch (origin) {
        case "PRODUCT":
          history.push(`/product/${finding.product?.id}`);
          break;
        case "SUBPRODUCT":
          history.push(`/subProduct/${finding.subProduct?.id}`);
          break;
        case "ASSETS":
          history.push(`/assets/${finding.armorcodeAssetId}`);
          break;
        case "TOOL":
          history.push("/security-tools");
          break;
        case "ASSESSMENT":
          history.push(`/assessments/${finding.assessments?.[0]?.id}`);
          break;
        default:
          break;
      }
    },
    [tagsMetaMap, tabValue, history, finding],
  );

  const onTagsActionClick = useCallback(
    (value: string | number, rowIndex: number, edit = false, currentRecord) => {
      switch (value) {
        case "openOrigin":
          OpenOrigin(currentRecord);
          break;
        case "edit":
          toggleEdit(rowIndex, edit);
          break;
        case "delete":
          onDelete(rowIndex);
      }
    },
    [OpenOrigin, onDelete, toggleEdit],
  );

  const onValuesChange = (value: React.Key) => {
    setTabValue(value as string);
  };

  const actionRenderer = useCallback(
    (currentRecord: Tag) => {
      // This is needed in case of sorting, The row gets sort but the row index always starts from 0
      // This calculate the record index from the array it resides
      const rowIndex = findIndex(
        dataSource as Tag[],
        (record: Tag) => record.id === currentRecord.id,
      );
      const permission = currentRecord.disable
        ? null
        : PermissionSetMap.UPDATE_FINDING_TAGS;
      const noAccessText = currentRecord.disable
        ? disableAccessText
          ? disableAccessText
          : `Cannot Edit - Inherited from ${getVocabulary(
              "product",
            )}/${getVocabulary("subProduct")}/Tool`
        : undefined;
      if (currentRecord.isEdit) {
        return (
          <>
            <AuthorisedContainer
              permission={permission as string}
              tooltip="Save Tag"
              noAccessText={noAccessText}
            >
              <ACButton
                onClick={() => onSave(rowIndex)}
                iconOnly
                type="outline"
                size="sm"
                style={{ marginRight: 8 }}
              >
                <SaveIcon />
              </ACButton>
            </AuthorisedContainer>
            <AuthorisedContainer
              permission={permission as string}
              tooltip="Cancel Edit"
              noAccessText={noAccessText}
            >
              <ACButton
                onClick={() => toggleEdit(rowIndex, false)}
                iconOnly
                size="sm"
                type="danger"
              >
                <TrashIcon />
              </ACButton>
            </AuthorisedContainer>
          </>
        );
      }
      return (
        <ACDropdown
          items={tagsActionItems(currentRecord)}
          menuWidth={120}
          placement="bottomRight"
          onOptionClick={(value: string | number) =>
            onTagsActionClick(value, rowIndex, true, currentRecord)
          }
        >
          <ACButton type="ghost" size="sm" iconOnly>
            <EllipsisVerticalIcon />
          </ACButton>
        </ACDropdown>
      );
    },
    [
      dataSource,
      disableAccessText,
      onSave,
      onTagsActionClick,
      tagsActionItems,
      toggleEdit,
    ],
  );

  const columns = useMemo(
    () => [
      {
        title: "Tag",
        dataIndex: "key",
        sorter: Sorters.STRING("key"),
        sortType: "string",
        key: "key",
        width: 200,
        render: (value: string, currentRecord: Tag) => {
          // This is needed in case of sorting, The row gets sort but the row index always starts from 0
          // This calculate the record index from the array it resides
          const tagKeys = tagValue.map((item) => item.key);
          const mergedArray =
            mentionOptions !== undefined
              ? [...mentionOptions, ...tagKeys]
              : [...tagKeys];

          if (currentRecord.isEdit) {
            const rowIndex = findIndex(
              dataSource as Tag[],
              (record: Tag) => record.id === currentRecord.id,
            );
            return (
              <AutoComplete
                size={props.size}
                // @ts-expect-error
                onKeyPress={preventEnter}
                style={{ width, maxWidth: "100%" }}
                defaultValue={value}
                name={`key_${rowIndex}`}
                onChange={(value) => onKeyChange(value, rowIndex)}
                filterOption={(inputValue, option) =>
                  ((option as DefaultOptionType).value as string)
                    .toUpperCase()
                    .indexOf(inputValue.toUpperCase()) !== -1
                }
              >
                {map(mergedArray, (option, index) => {
                  return (
                    <AutoComplete.Option
                      key={`${option}_${index}`}
                      value={option}
                    >
                      {option}
                    </AutoComplete.Option>
                  );
                })}
              </AutoComplete>
            );
          }
          return <ACTag label={value} className="ac-tag-table-key" />;
        },
      },
      {
        title: "Value",
        dataIndex: "value",
        key: "value",
        width: 200,
        sorter: true,
        render: (value: string, currentRecord: Tag) => {
          // This is needed in case of sorting, The row gets sort but the row index always starts from 0
          // This calculate the record index from the array it resides
          // Use currentRecord.key directly - it's updated in real-time by onKeyChange
          const tagValues = flatten(
            tagValue
              .filter((record) => record.key === currentRecord.key)
              .map((record) => record.value),
          );
          const mergedArray =
            mentionOptions !== undefined
              ? [...tagValues, ...mentionOptions]
              : [...tagValues];
          if (currentRecord.isEdit) {
            const rowIndex = findIndex(
              dataSource as Tag[],
              (record: Tag) => record.id === currentRecord.id,
            );
            return (
              <AutoComplete
                size={props.size}
                //@ts-expect-error
                name={`value_${rowIndex}`}
                defaultValue={value}
                onKeyPress={preventEnter}
                onChange={(value) => onKeyInputChange(rowIndex, value)}
                style={{ width, maxWidth: "100%" }}
              >
                {map(mergedArray, (option, index) => {
                  return (
                    <AutoComplete.Option
                      key={`${option}_${index}`}
                      value={option}
                    >
                      {option}
                    </AutoComplete.Option>
                  );
                })}
              </AutoComplete>
            );
          }
          return isEmpty(value) ? (
            <span className="ac-tag-table-value-input">-</span>
          ) : (
            value
          );
        },
      },
      ...extraColumn(tabValue),
    ],
    [
      dataSource,
      onKeyInputChange,
      mentionOptions,
      onKeyChange,
      props.size,
      tagValue,
      width,
      tabValue,
      extraColumn,
    ],
  );
  const {
    //@ts-expect-error
    requiredError,
    //@ts-expect-error
    duplicateError,
    //@ts-expect-error
    colonError,
    //@ts-expect-error
    reservedKeyError,
    //@ts-expect-error
    accountAdminOnlyError,
    //@ts-expect-error
    lengthError,
  } = currentRowRef.current;

  const filteredDataSource = useMemo(() => {
    if (tabValue === "FINDING") {
      return dataSource.filter((item) => {
        const keyValue = `${(item as Tag).key}:${(item as Tag).value}`;
        const isIncludedInTagsMetaMap =
          (!isUndefined(tagsMetaMap["FINDING"]) &&
            (tagsMetaMap["FINDING"].includes(keyValue) ||
              tagsMetaMap["FINDING"].includes((item as Tag).key))) ||
          (!isUndefined(tagsMetaMap["SCAN"]) &&
            (tagsMetaMap["SCAN"].includes(keyValue) ||
              tagsMetaMap["SCAN"].includes((item as Tag).key))) ||
          (!isUndefined(tagsMetaMap["CTI"]) &&
            (tagsMetaMap["CTI"].includes(keyValue) ||
              tagsMetaMap["CTI"].includes((item as Tag).key)));
        const isAssessmentMatch =
          tagsMetaMap["armorcode.assessmentname"]?.includes(keyValue) ||
          tagsMetaMap["armorcode.assessmentname"]?.includes((item as Tag).key);
        const hasEditProperty = item.hasOwnProperty("isEdit");
        return isIncludedInTagsMetaMap || isAssessmentMatch || hasEditProperty;
      });
    }
    if (
      ["ASSETS", "TOOL", "PRODUCT", "SUBPRODUCT", "ASSESSMENT", "FEED"].indexOf(
        tabValue as string,
      ) !== -1
    ) {
      if (!isUndefined(tagsMetaMap[tabValue])) {
        return tagsMetaMap[tabValue].map((tag: string) => {
          const [key, ...rest] = tag.split(":");
          return { key, value: rest.join(":"), id: tag, disable: true } as Tag;
        });
      } else {
        return [];
      }
    }
    return dataSource;
  }, [dataSource, tabValue, tagsMetaMap]);
  // TODO - move all logic in hook so that we can use if separately for finding tags
  const sortedDataSource = useMemo(() => {
    const newAddedRecord = filteredDataSource
      ? filteredDataSource.filter((item) => item.newRecordAdded)
      : [];
    const oldRecord = filteredDataSource
      ? filteredDataSource.filter((item) => !item.newRecordAdded)
      : [];
    if (!isEmpty(sortInfo)) {
      const sorters: { [key: string]: SorterFunction<Tag> } = {
        key: Sorters.STRING("key"),
        value: Sorters.STRING("value"),
        origin: Sorters.STRING("origin"),
      };
      const addOrigin = (filteredDataSource: Tag[]) => {
        return filteredDataSource.map((item) => {
          const value = getOrigin(item, tagsMetaMap);
          return {
            ...item,
            origin: find(tabsList, { value: value })?.label,
          };
        });
      };

      const sortedStats = addOrigin([...oldRecord]);
      sortedStats.sort((a, b) => {
        let comparison = 0;
        for (const i in sortInfo) {
          if (sorters[i]) {
            comparison =
              comparison ||
              sorters[i](a, b) * (sortInfo[i] === "ascend" ? 1 : -1);
          }
        }
        return comparison;
      });
      return [...sortedStats, ...addOrigin(newAddedRecord)];
    }
    return [...oldRecord, ...newAddedRecord];
  }, [sortInfo, filteredDataSource, tagsMetaMap, tabsList]);

  const searchFilteredDataSource = useMemo(() => {
    if (!searchTerm) return sortedDataSource;
    const term = searchTerm.toLowerCase();
    return sortedDataSource.filter(
      (tag) =>
        tag.isEdit ||
        tag.newRecordAdded ||
        tag.key?.toLowerCase().includes(term) ||
        tag.value?.toLowerCase().includes(term),
    );
  }, [sortedDataSource, searchTerm]);

  const filteredTabList = useMemo(() => {
    return filter(tabsList, (tab: ACTabItem) => {
      return !tab.optional || tagsMetaMap[tab.value]?.length > 0;
    });
  }, [tabsList, tagsMetaMap]);

  const { columns: customizedColumns } = useTableCustomizationHelper<Tag>({
    columns,
    prefKey: "ac-tag-table-old",
    skipColumnVisibility: true,
    draggable: false,
  });

  return (
    <div ref={wrapperRef} className="ac-tag-table">
      <ACTable<Tag>
        fullPage
        dataSource={searchFilteredDataSource}
        pagination={false}
        columns={customizedColumns}
        onSortChange={onSortChange}
        sortInfo={sortInfo}
        rowKey="id"
        //@ts-expect-error
        onRow={(record, rowIndex) => ({
          "data-automation-row-key": rowIndex,
        })}
        components={{
          header: {
            cell: ACResizableColumnCell,
          },
        }}
        computeTableWidth
        autoEnableScroll
        actionRender={editable ? actionRenderer : undefined}
        actionColumnWidth={60}
        footer={() =>
          editable &&
          ["all", "finding"].includes((tabValue as string).toLowerCase()) && (
            <>
              <div
                className="danger"
                style={{
                  width: "70%",
                }}
              >
                {requiredError
                  ? "Please fill all the required field"
                  : duplicateError
                    ? "Please enter unique key and value pair"
                    : colonError
                      ? "The field should not contain colon"
                      : reservedKeyError
                        ? "This key is reserved"
                        : accountAdminOnlyError
                          ? "Only Account Admins can add/edit/remove armorcode.group tags"
                          : lengthError
                            ? "key+value length should be less than 996 characters."
                            : ""}
              </div>
              <AuthorisedContainer
                permission={PermissionSetMap.UPDATE_FINDING_TAGS}
              >
                <ACButton
                  type="outline"
                  onClick={onAdd}
                  size="sm"
                  leftIcon={<PlusIcon />}
                >
                  Add Tag
                </ACButton>
              </AuthorisedContainer>
            </>
          )
        }
        {...(filteredTabList
          ? {
              title: () => (
                <ACTabs
                  items={filteredTabList}
                  value={tabValue}
                  onChange={onValuesChange}
                  size="sm"
                />
              ),
            }
          : {})}
      />
    </div>
  );
}
